import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getAllSupportPractitionerAccessLogs } from 'src/supportPortal/services/practitionerOnboardingService'
import PractitionerAccessLogList from 'src/supportPortal/components/common/mentor/PractitionerAccessLogList'

const TYPE_TABS = [
  { id: 'all', label: 'All' },
  { id: 'therapist', label: 'Therapists' },
  { id: 'doctor', label: 'Doctors' },
  { id: 'screener', label: 'Screeners' },
]

const ACTION_FILTERS = [
  { id: 'all', label: 'All actions' },
  { id: 'ENABLED', label: 'Enabled' },
  { id: 'DISABLED', label: 'Disabled' },
]

const PAGE_SIZE = 50

/**
 * Platform-wide practitioner enable/disable access log — a single place to review who
 * paused or re-enabled which practitioner, when, and why. Per-practitioner trails also
 * live on each practitioner's detail page.
 */
export default function PractitionerAccessLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeType = (searchParams.get('type') || 'all').toLowerCase()
  const activeAction = searchParams.get('action') || 'all'
  const page = Math.max(parseInt(searchParams.get('page') || '0', 10) || 0, 0)

  const [state, setState] = useState({ loading: true, error: '', logs: [], total: 0 })

  useEffect(() => {
    let cancelled = false
    async function load() {
      setState((prev) => ({ ...prev, loading: true, error: '' }))
      const resp = await getAllSupportPractitionerAccessLogs({
        type: activeType === 'all' ? undefined : activeType,
        action: activeAction === 'all' ? undefined : activeAction,
        page,
        size: PAGE_SIZE,
      })
      if (cancelled) return
      if (resp.ok) {
        setState({ loading: false, error: '', logs: resp.data.logs, total: resp.data.total })
      } else {
        setState({
          loading: false,
          error: resp.message || 'Could not load practitioner access logs.',
          logs: [],
          total: 0,
        })
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [activeType, activeAction, page])

  function updateParams(next) {
    const params = new URLSearchParams(searchParams)
    Object.entries(next).forEach(([key, value]) => {
      if (value === null || value === undefined || value === 'all' || value === 0 || value === '0') {
        params.delete(key)
      } else {
        params.set(key, String(value))
      }
    })
    setSearchParams(params, { replace: false })
  }

  const totalPages = Math.max(Math.ceil(state.total / PAGE_SIZE), 1)

  return (
    <>
      <SupportHeader heading="Practitioner Access Logs" showBackButton />
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Every enable / disable action taken on a practitioner across the platform — newest
          first. Each practitioner's own trail is also on their detail page.
        </Typography>

        <Tabs
          value={activeType}
          onChange={(_e, next) => updateParams({ type: next, page: 0 })}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 1.5 }}
        >
          {TYPE_TABS.map((tab) => (
            <Tab key={tab.id} value={tab.id} label={tab.label} />
          ))}
        </Tabs>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {ACTION_FILTERS.map((filter) => (
            <Chip
              key={filter.id}
              label={filter.label}
              color={activeAction === filter.id ? 'primary' : 'default'}
              variant={activeAction === filter.id ? 'filled' : 'outlined'}
              onClick={() => updateParams({ action: filter.id, page: 0 })}
            />
          ))}
        </Stack>

        {state.loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : state.error ? (
          <Alert severity="error">{state.error}</Alert>
        ) : (
          <>
            <PractitionerAccessLogList logs={state.logs} showPractitioner />
            {state.total > PAGE_SIZE && (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ mt: 2.5 }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page <= 0}
                  onClick={() => updateParams({ page: page - 1 })}
                >
                  Previous
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Page {page + 1} of {totalPages}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page + 1 >= totalPages}
                  onClick={() => updateParams({ page: page + 1 })}
                >
                  Next
                </Button>
              </Stack>
            )}
          </>
        )}
      </Box>
    </>
  )
}
